/*
 * Calli motion layer.
 * Progressive enhancement only: every section is fully readable without this file.
 * Respects prefers-reduced-motion and re-initialises inside the theme editor.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const finePointer = window.matchMedia('(pointer: fine)');

  let lenis = null;
  let lenisRaf = 0;
  const scrollHandlers = new Set();

  /*
   * Horizon scrolls `.page-wrapper` instead of the window from 990px up (base.css),
   * so smooth scroll has to attach to whichever element is the real scroller.
   */
  function getScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && getComputedStyle(wrapper).overflowY === 'auto') return wrapper;
    return window;
  }

  /* ---------- Smooth scroll (mouse wheel only; touch stays native) ---------- */
  function destroySmoothScroll() {
    if (!lenis) return;
    cancelAnimationFrame(lenisRaf);
    lenis.destroy();
    lenis = null;
  }

  function initSmoothScroll() {
    destroySmoothScroll();
    if (reduceMotion.matches || !finePointer.matches || typeof window.Lenis !== 'function') return;
    if (window.Shopify && window.Shopify.designMode) return;

    const scroller = getScroller();
    const options = {
      lerp: 0.11,
      // Never hijack scrolling inside drawers, dialogs, menus or anything marked to opt out.
      prevent: (node) => !!node.closest('dialog, [data-lenis-prevent], .cart-drawer, .menu-drawer, details[open] > *'),
    };
    if (scroller !== window) {
      options.wrapper = scroller;
      options.content = scroller;
    }
    lenis = new window.Lenis(options);
    const raf = (time) => {
      lenis.raf(time);
      lenisRaf = requestAnimationFrame(raf);
    };
    lenisRaf = requestAnimationFrame(raf);
  }

  // Pause smooth scroll while the theme locks scrolling (Horizon sets html[scroll-lock] for drawers and modals).
  new MutationObserver(() => {
    if (!lenis) return;
    root.hasAttribute('scroll-lock') ? lenis.stop() : lenis.start();
  }).observe(root, { attributes: true, attributeFilter: ['scroll-lock'] });

  /*
   * In-page links (#reviews, #calli-buy...). Lenis only handles anchors when it scrolls the window,
   * and here it scrolls `.page-wrapper`, so route them through Lenis ourselves. Without Lenis the
   * browser jumps natively and `scroll-margin-top` in calli.css keeps the target clear of the header.
   */
  document.addEventListener('click', (e) => {
    if (!lenis || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const link = e.target.closest('a[href^="#"]');
    const id = link && decodeURIComponent(link.getAttribute('href').slice(1));
    const target = id && document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    const header = document.querySelector('#header-group, .header-section, header');
    const offset = header ? Math.min(header.getBoundingClientRect().height, 140) + 12 : 24;
    lenis.scrollTo(target, { offset: -offset });
    history.replaceState(null, '', `#${id}`);
  });

  /* ---------- One scroll loop for everything ---------- */
  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      scrollHandlers.forEach((fn) => fn());
    });
  }
  // Capture phase catches scrolls from the window and from `.page-wrapper` alike.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });
  desktop.addEventListener('change', initSmoothScroll);

  /* Progress of an element through the viewport: 0 when its top hits the bottom, 1 when its bottom hits the top. */
  function progressOf(el) {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    return Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
  }

  /* ---------- Word splitting (keeps <em>, <strong>, links intact) ---------- */
  function splitWords(el, wrapClass) {
    if (el.dataset.splitDone) return el.querySelectorAll(`.${wrapClass}`);
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const outer = document.createElement('span');
            outer.className = wrapClass;
            outer.style.setProperty('--w', i++);
            if (wrapClass === 'w') {
              const inner = document.createElement('span');
              inner.textContent = part;
              outer.appendChild(inner);
            } else {
              outer.textContent = part;
            }
            frag.appendChild(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    el.dataset.splitDone = 'true';
    return el.querySelectorAll(`.${wrapClass}`);
  }

  /* ---------- Reveal on enter ---------- */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        if (entry.target.matches('[data-count-scope]')) runCounters(entry.target);
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
  );

  function runCounters(scope) {
    scope.querySelectorAll('[data-count]').forEach((el) => {
      const target = parseFloat(el.dataset.count);
      if (reduceMotion.matches || !isFinite(target)) {
        el.textContent = el.dataset.count;
        return;
      }
      const start = performance.now();
      const dur = 1600;
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 4);
        el.textContent = Math.round(target * eased).toLocaleString('en-SG');
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  /* ---------- Story statement: words light up as you scroll ---------- */
  function initStatement(el) {
    const words = splitWords(el, 'sw');
    if (reduceMotion.matches) {
      words.forEach((w) => w.classList.add('is-lit'));
      return;
    }
    scrollHandlers.add(() => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Fully lit by the time the paragraph's bottom reaches 40% of the viewport.
      const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.45)));
      const lit = Math.round(p * words.length);
      words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
    });
  }

  /* ---------- Timeline: rail fills and milestones light up as you scroll ---------- */
  function initTimeline(list) {
    const items = [...list.querySelectorAll('.calli-timeline__item')];
    if (reduceMotion.matches) {
      items.forEach((item) => item.classList.add('is-active'));
      return;
    }
    scrollHandlers.add(() => {
      const r = list.getBoundingClientRect();
      const line = window.innerHeight * 0.62; // the "reading line" the fill chases
      const p = Math.min(1, Math.max(0, (line - r.top) / r.height));
      list.style.setProperty('--progress', p.toFixed(4));
      let current = null;
      items.forEach((item) => {
        const dot = item.querySelector('.calli-timeline__dot');
        const d = dot.getBoundingClientRect();
        const reached = d.top + d.height / 2 < line;
        item.classList.toggle('is-active', reached);
        if (reached) current = item;
      });
      // The most recently reached milestone is "current" and gets the extra lift and glow.
      items.forEach((item) => item.classList.toggle('is-current', item === current));
    });
  }

  /* ---------- Generic vertical parallax for decorative elements ---------- */
  function initParallax(el) {
    if (reduceMotion.matches) return;
    const speed = parseFloat(el.dataset.parallax) || 0.15;
    const host = el.parentElement;
    scrollHandlers.add(() => {
      const p = progressOf(host);
      el.style.transform = `translate3d(0, ${(0.5 - p) * speed * window.innerHeight}px, 0)`;
    });
  }

  /* ---------- Looping step videos: load near the viewport, play only while visible ---------- */
  const videoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target: video, isIntersecting }) => {
        if (isIntersecting) {
          if (!video.dataset.loaded) {
            video.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
            video.dataset.loaded = 'true';
            video.load();
          }
          if (!reduceMotion.matches) video.play().catch(() => {}); // Low Power Mode may block autoplay; the poster stays.
        } else if (!video.paused) {
          video.pause();
        }
      });
    },
    { rootMargin: '300px 0px' }
  );

  /* ---------- Section bootstrapping ---------- */
  function initSection(scope) {
    scope.querySelectorAll('[data-split]').forEach((el) => {
      splitWords(el, 'w');
      el.classList.add('calli-split');
      revealObserver.observe(el);
    });
    scope.querySelectorAll('[data-reveal]').forEach((el, i) => {
      if (!el.style.getPropertyValue('--reveal-i')) el.style.setProperty('--reveal-i', el.dataset.reveal || 0);
      revealObserver.observe(el);
    });
    scope.querySelectorAll('[data-count-scope]').forEach((el) => revealObserver.observe(el));
    scope.querySelectorAll('[data-statement]').forEach(initStatement);
    scope.querySelectorAll('[data-calli-timeline]').forEach(initTimeline);
    scope.querySelectorAll('[data-parallax]').forEach(initParallax);
    scope.querySelectorAll('video[data-lazy-video]').forEach((v) => videoObserver.observe(v));
    onScroll();
  }

  function boot() {
    initSmoothScroll();
    initSection(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-run when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', (e) => initSection(e.target));

  // Safety net: never leave content hidden if something above failed.
  window.addEventListener('load', () => {
    setTimeout(() => {
      document.querySelectorAll('[data-reveal]:not(.is-in), [data-split]:not(.is-in)').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
      });
    }, 2500);
  });
})();
